import { useState } from 'react'
import { GRID, INK, INK_MID } from './chartColors.js'
import { round } from './scales.js'

export interface StackedLayer {
  key: string
  name: string
  color: string
  /** One value per period; null for a period with nothing. */
  values: readonly (number | null)[]
}

interface StackedBarsProps {
  periods: readonly string[]
  layers: readonly StackedLayer[]
  /** Each period's bar as a share of its total (100%) rather than its total. */
  normalized?: boolean
  periodLabel: (period: string) => string
  format: (value: number) => string
  title: string
  height?: number
}

const WIDTH = 600
const PAD = { top: 16, right: 12, bottom: 24, left: 40 }
const GAP = 2

/** Stacked columns, one per month, a 2px gap between segments; the pointer over a column shows each layer's value. */
export function StackedBars({ periods, layers, normalized = false, periodLabel, format, title, height = 240 }: StackedBarsProps) {
  const [hover, setHover] = useState<number | null>(null)
  const plotWidth = WIDTH - PAD.left - PAD.right
  const plotHeight = height - PAD.top - PAD.bottom
  const slot = plotWidth / Math.max(1, periods.length)
  const barWidth = Math.min(40, slot * 0.7)
  const totals = periods.map((_, index) => layers.reduce((sum, layer) => sum + (layer.values[index] ?? 0), 0))
  const max = normalized ? 100 : Math.max(1, ...totals)
  const scale = (value: number): number => (value / max) * plotHeight
  const ticks = normalized ? [0, 50, 100] : [0, Math.round(max / 2), Math.round(max)]
  return (
    <svg role="img" aria-label={title} viewBox={`0 0 ${WIDTH} ${height}`} className="block w-full" onPointerLeave={() => setHover(null)}>
      <desc>
        {title}. Latest month: {layers.map((layer) => `${layer.name} ${format(layer.values[periods.length - 1] ?? 0)}`).join('; ')}.
      </desc>
      {ticks.map((tick) => (
        <g key={tick}>
          <line x1={PAD.left} y1={round(PAD.top + plotHeight - scale(tick))} x2={WIDTH - PAD.right} y2={round(PAD.top + plotHeight - scale(tick))} stroke={GRID} strokeWidth={1} />
          <text x={PAD.left - 6} y={round(PAD.top + plotHeight - scale(tick)) + 4} textAnchor="end" fontSize={11} fill={INK_MID}>
            {normalized ? `${tick}%` : format(tick)}
          </text>
        </g>
      ))}
      {periods.map((period, index) => {
        const total = totals[index] ?? 0
        const left = PAD.left + slot * index + (slot - barWidth) / 2
        let stacked = 0
        return (
          <g key={period} onPointerEnter={() => setHover(index)}>
            <rect x={round(left - 2)} y={PAD.top} width={round(barWidth + 4)} height={plotHeight} fill="transparent" />
            {layers.map((layer) => {
              const raw = layer.values[index] ?? 0
              const value = normalized ? (total > 0 ? (raw / total) * 100 : 0) : raw
              const h = scale(value)
              const top = PAD.top + plotHeight - stacked - h
              stacked += h
              return h <= 0 ? null : <rect key={layer.key} x={round(left)} y={round(top + (h > GAP ? GAP / 2 : 0))} width={round(barWidth)} height={round(Math.max(0, h - (h > GAP ? GAP : 0)))} fill={layer.color} rx={1} />
            })}
            <text x={round(left + barWidth / 2)} y={height - 6} textAnchor="middle" fontSize={11} fill={INK_MID}>
              {periods.length <= 13 || index % Math.ceil(periods.length / 12) === 0 ? periodLabel(period) : ''}
            </text>
          </g>
        )
      })}
      {hover !== null && periods[hover] && (
        <foreignObject x={hover < periods.length / 2 ? round(PAD.left + slot * hover + slot) : round(PAD.left + slot * hover - 180)} y={PAD.top} width={176} height={12 + 18 * (layers.length + 1)} style={{ overflow: 'visible' }}>
          <div style={{ background: INK, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 11, lineHeight: '18px', whiteSpace: 'nowrap' }}>
            <div style={{ color: '#B8D4DC' }}>{periodLabel(periods[hover])}</div>
            {layers.map((layer) => {
              const raw = layer.values[hover] ?? 0
              const total = totals[hover] ?? 0
              return (
                <div key={layer.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: layer.color, display: 'inline-block', borderRadius: 2 }} />
                    {layer.name}
                  </span>
                  <strong>{normalized && total > 0 ? `${Math.round((raw / total) * 100)}%` : format(raw)}</strong>
                </div>
              )
            })}
          </div>
        </foreignObject>
      )}
    </svg>
  )
}
